import { CheckboxWithLabel } from "../../../../common/components/CheckboxWithLabel";
import { useCallback, useMemo } from "react";
import { type TileSelectProps } from "../TileSelect/TileSelect";
import classNames from "classnames";
import { Controller, useFormContext } from "react-hook-form";
import { useHSAFacetData } from "../../useHSAFacetData";
import { type ItemDefinitionV2, type RuntimeFilterItem } from "~/HousingSelectionAssistant/types/FilterDefinitionV2";

export interface CheckboxSelectProps<T extends ItemDefinitionV2 = ItemDefinitionV2> {
  className?: string;

  /*
      multiple = false - string
      multiple = true - string[]
   */
  value: unknown;
  values: T[];

  label?: string;

  // If true, value will be an array of codes, otherwise a string with the active code
  multiple?: boolean;

  onChange?: (newVal: unknown | null) => void;

  "data-test-id"?: string;

  columnAmount?: number;
}

export const CheckboxSelect = ({
  className,
  label,
  value,
  values,
  multiple,
  onChange,
  "data-test-id": testId,
  columnAmount = 1,
}: TileSelectProps) => {
  const onElementClick = useOnElementClick(value, onChange, multiple);

  const cols = useMemo(() => {
    if (columnAmount === 1) {
      return [values];
    }
    return splitIntoCols(values, columnAmount);
  }, [values, columnAmount]);

  return (
    <div className={classNames("scha__checkbox-select", className)} data-test-id={testId}>
      {label && <label className={"scha__checkbox-select__label"}>{label}</label>}
      <div className={"scha__checkbox-select__checkbox-grid"}>
        {cols.map((columns, index) => (
          <div key={index} className="scha__checkbox-select__checkbox-list">
            {columns.map((tileDef) => (
              <CheckboxWithLabel
                key={tileDef.code}
                className={"flex-align-center"}
                checked={isActive(value, tileDef.code)}
                onCheck={() => onElementClick(tileDef.code)}
                data-test-id={`${testId}-${tileDef.code}`}
                disabled={tileDef.disabled && !isActive(value, tileDef.code)}
              >
                {tileDef.label}
              </CheckboxWithLabel>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export function useOnElementClick(
  value: unknown,
  onChange: ((newVal: unknown | null) => void) | undefined,
  multiple: boolean | undefined
) {
  return useCallback(
    (code: string) => {
      if (!onChange) {
        return;
      }
      if (!multiple) {
        if (value === code) {
          onChange(null);
        }
        onChange(code);
      } else {
        const arr = (value ?? []) as string[];
        if (arr.includes(code)) {
          onChange(arr.filter((v) => v !== code));
        } else {
          onChange(arr.concat(code));
        }
      }
    },
    [onChange, value, multiple]
  );
}

export function isActive(activeValues: unknown, tileValue: string) {
  const val = activeValues as string[] | string;
  const arr = Array.isArray(val) ? val : [val];
  return arr.includes(tileValue);
}

export interface RHFCheckboxSelectProps {
  className?: string;

  values: RuntimeFilterItem[];

  label?: string;

  // If true, value will be an array of codes, otherwise a string with the active code
  multiple?: boolean;

  name: string;

  "data-test-id"?: string;

  columnAmount?: number;
}

export const RHFCheckboxSelect = ({ name, ...rest }: RHFCheckboxSelectProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      render={({ field: { value, onChange } }) => <CheckboxSelect value={value} onChange={onChange} {...rest} />}
      control={control}
      name={name}
    />
  );
};

export interface HousingSACheckboxSelectProps extends Omit<RHFCheckboxSelectProps, "values" | "label"> {}

export const HousingSACheckboxSelect = (props: HousingSACheckboxSelectProps & { omitLabel?: boolean }) => {
  const { values, label } = useHSAFacetData(props);

  return <RHFCheckboxSelect values={values} label={label} {...props} />;
};

function splitIntoCols<T extends any>(list: T[], colAmount: number) {
  const amountPerCol = Math.floor(list.length / colAmount);
  let remainder = list.length % colAmount;
  let start = 0;

  const chunks: T[][] = [];

  for (let i = 0; i < colAmount; i++) {
    const size = amountPerCol + (remainder > 0 ? 1 : 0);
    remainder -= 1;
    chunks.push(list.slice(start, start + size));
    start += size;
  }
  return chunks;
}
